import * as React from 'react';
import { TextField } from '@mui/material';
import ConfirmationButtons from '../button/ConfirmationButtons';
import TestKitsDropDownMultiselect from '../drop-down/TestKitsDropdownMultiSelect';
import ApiService from 'utils/services/ApiService';
import { usePopupMessage } from '../../hooks/SendPopUpMessage';
import { ILocations } from 'utils/models/locations';
import {
	getTestResults,
	saveSyncCache,
	saveTestResults,
} from '../../utils/services/idbService';
import { useDispatch } from 'react-redux';
import { setUpdatedLocations } from "utils/redux/reducers/updateLocationReducer";
import { setAllSitesData } from 'utils/redux/reducers/allSitesLoadedReducer';
import { addNewLocation as addNewLocationAction } from 'utils/redux/reducers/updateLocationReducer';

interface AddNewLocationProps {
	headers: any[];
	setShowAddNewLocation: React.Dispatch<React.SetStateAction<boolean>>;
	locations: any[];
	testKits: any[];
	siteId: number;
	userEmail?: string;
	userId: number;
	setLocations: React.Dispatch<React.SetStateAction<any[]>>;
	registerPendingCreate: (tempId: number) => {
		resolve: (realId: number) => void;
		reject: (error: any) => void;
	};
}
const formatTestKitsDescriptors = (testKitsToChange: any[]) => {
    return testKitsToChange.map(testKit => {
        return {
            testKitName: testKit.testKitName,  // Include the test kit name
            testKitDescription: testKit.testKitDescription || '',  // Include the description or an empty string if not present
            id: testKit.id,  // Include the test kit ID
            status: testKit.status || 'VALIDATED',  // Include the status or 'VALIDATED' if not present
            ownerOrganizationId: testKit.ownerOrganizationId || ''  // Include the owner organization ID or an empty string if not present
        };
    });
};
const AddNewLocation: React.FC<AddNewLocationProps> = ({
	headers,
	setShowAddNewLocation,
	locations,
	testKits,
	siteId,
	userEmail,
	userId,
	setLocations,
	registerPendingCreate,
}) => {
	const sendPopUpMessage = usePopupMessage();
	const [showConfirmationLoading, setShowConfirmationLoading] =
		React.useState(false);
	const [newLocation, setNewLocation] = React.useState({
		name: '',
		testKitDescriptors: [] as any[],
	});
  const dispatch = useDispatch();

	const [locationError, setLocationError] = React.useState('');
	const [testKitsError, setTestKitsError] = React.useState('');
	const [selectedTestKits, setSelectedTestKits] = React.useState<any[]>([]);

	const resetErrors = () => {
		setLocationError('');
		setTestKitsError('');
		setNewLocation(() => ({
			...newLocation,
			name: '',
		}));
	};

	const handleLocationName = (e: React.ChangeEvent<HTMLInputElement>) => {
		resetErrors();
		setNewLocation(() => ({
			...newLocation,
			name: e.target.value,
		}));
	};

	const addNewLocation = async () => {
		setShowConfirmationLoading(true);
		try {
			if (!navigator.onLine) {
				const allSitesData = await getTestResults(
					`allSites`,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				const SiteIndex = allSitesData?.findIndex((site: any)=> site.id === siteId)

				const tempId = new Date().getTime();
				const locationDataCached = {
					id: tempId,
					siteFK: siteId,
					name: newLocation.name.trim(),
					testKitNames: newLocation.testKitDescriptors.map(tk => tk.testKitName),
					customAlarmVOs: [],
					action: 'CREATE'
				};

				// Save to sync cache
				await saveSyncCache(locationDataCached as any, "locationSyncCache");

				const reduxLocation = {
					id: tempId,
					dataPoints:[],
					dataPointAllVOs: [],
					sortOrder: 9999,
					name: newLocation.name.trim(),
					testKitDescriptors: newLocation.testKitDescriptors,
					testKitIDs: newLocation.testKitDescriptors.map(tk => tk.id),
					customAlarmVOs: [],
				}
				const site = allSitesData[SiteIndex]
				site.sampleLocationAllVOs.push(reduxLocation as any)

				//Table columns offline handling
				const username = localStorage.getItem('currentUser');
				if (username) {
					let incomingColumns: { colName: any; colType: string; desktop: boolean; mobile: boolean; siteId: number; }[] = [];

					//Convert to column form
					selectedTestKits.forEach(testKit => {
						testKit.fieldDescriptorAllVOs.forEach((desc: { outputName: any; hide: boolean; type: string;}) => {
							if (!desc.hide && "Output" === desc.type) {
								incomingColumns.push({
									colName: desc.outputName,
									colType: "Output",
									desktop: true,
									mobile: true,
									siteId: siteId,
								});
							} else if ("Output" !== desc.type) {
								incomingColumns.push({
									colName: desc.outputName,
									colType: "Input",
									desktop: false,
									mobile: false,
									siteId: siteId,
								});
							}
						});
					});

					let columnsDataCached = await getTestResults(
						`columns_data_${siteId}`,
						username
					);

					//If there are new columns, add them to the cache.
					if (incomingColumns.length > 0) {
						let incomingAndCachedCols = incomingColumns;
						if (columnsDataCached) {
							incomingAndCachedCols = incomingColumns.concat(columnsDataCached);
						}
						await saveTestResults(
							`columns_data_${siteId}`,
							incomingAndCachedCols,
							username
						);
					}

					//Update the table columns of the site while you're here
					let existingCols = site.tableColumnSettingsAllVOs;
					incomingColumns.forEach((newCol: any) => {
						let prev = existingCols.find((existingCol: any) => existingCol.colName === newCol.colName);
						if (!prev) {
							existingCols.push({
								colName: newCol.colName,
								colType: newCol.colType,
								visible: newCol.desktop
							})
						}
					})
				}

				await saveTestResults(
					`allSites`,
					allSitesData,
					localStorage.getItem('currentUser') ?? 'unknown'
				);

				// Update Redux with proper test kit data
				dispatch(addNewLocationAction(reduxLocation as any));

				dispatch(setAllSitesData(allSitesData));

				sendPopUpMessage('Success', 'Location added successfully. Will sync when online.');
			} else {
				// Existing online mode code
				const dataToSave = {
					siteID: siteId,
					testKitDescriptorVOs: newLocation.testKitDescriptors,
					locationName: newLocation.name.trim(),
					creationDate: new Date().toISOString(),
					modificationDate: new Date().toISOString(),
					samplePointCreator: userEmail,
					samplePointEditor: userEmail,
					status: 'Active',
				};

				const tempId = new Date().getTime();
				const optimisticLocation = {
					id: tempId,
					dataPoints: [],
					dataPointAllVOs: [],
					sortOrder: 9999,
					name: newLocation.name.trim(),
					testKitDescriptors: newLocation.testKitDescriptors,
					testKitIDs: newLocation.testKitDescriptors.map(tk => tk.id),
					customAlarmVOs: [],
				};
				//
				setLocations((prev) => [...prev, optimisticLocation]);
				const pendingCreate = registerPendingCreate(tempId);

				const username = localStorage.getItem('currentUser');

				if (username) {
					let incomingColumns: { colName: any; colType: string; desktop: boolean; mobile: boolean; siteId: number; }[] = [];

					//Convert to column form
					selectedTestKits.forEach(testKit => {
						testKit.testKitOutputDescriptorVOs.forEach((outDesc: { outputName: any; hide: boolean; }) => {
							if (!outDesc.hide) {
								incomingColumns.push({
									colName: outDesc.outputName,
									colType: "Output",
									desktop: true,
									mobile: true,
									siteId: siteId,
								});
							}
						});
						testKit.testKitInputDescriptorVOs.forEach((inDesc: { inputName: any; }) => {
							incomingColumns.push({
								colName: inDesc.inputName,
								colType: "Input",
								desktop: false,
								mobile: false,
								siteId: siteId,
							});
						});
					});

					let columnsDataCached = await getTestResults(
						`columns_data_${siteId}`,
						username
					);

					//Check if the columns are already cached.
					//This breaks adding new sample locations, need to change it to update the monolith cache instead.

					// incomingColumns = incomingColumns.filter(incomingCol => {
					// 	let keep = true;
					// 	columnsDataCached.forEach((existingColumn: { colName: any; }) => {
					// 		if (incomingCol.colName === existingColumn.colName) {
					// 			keep = false;
					// 		}
					// 	});
					// 	return keep;
					// });

					//If there are new columns, add them to the cache.
					if (incomingColumns.length > 0) {
						let incomingAndCachedCols = incomingColumns;
						if (columnsDataCached) {
							incomingAndCachedCols = incomingColumns.concat(columnsDataCached);
						}
						await saveTestResults(
							`columns_data_${siteId}`,
							incomingAndCachedCols,
							username
						);
					}

					let testKitIDs = [] as number[];
					for (let desc of newLocation.testKitDescriptors) {
						testKitIDs.push(desc.id);
					}

					let locationVO = {
						id: tempId,
						name: newLocation.name.trim(),
						customAlarmVOs: [],
						dataPointAllVOs: [],
						testKitIDs: testKitIDs,
					}

					await addNewLocationToCache(locationVO, incomingColumns);
				}

				ApiService.addNewLocation(dataToSave)
					.then(async (result) => {
						if (result.status !== 200)
							throw Error('Error to connect with the server.');

						await reconcileLocationIdInCache(tempId, result.data.id);

						setLocations((prev) =>
							prev.map((loc) =>
								loc.id === tempId
									? {
											...result.data,
											...loc,
											id: result.data.id,
									  }
									: loc
							)
						);

						pendingCreate.resolve(result.data.id);
					})
					.catch((error) => {
						console.error(error);
						setLocations((prev) => prev.filter((loc) => loc.id !== tempId));
						pendingCreate.reject(error);
						sendPopUpMessage('Error', 'Error adding new sample location');
					});
			}
		} catch (error) {
			console.error(error);
			sendPopUpMessage('Error', 'Error adding new sample location');
		} finally {
			setShowConfirmationLoading(false);
			setShowAddNewLocation(false);
		}
	};
	//Update the tempID to the real ID now that the server has responded.
	async function reconcileLocationIdInCache(tempId: number, realId: number) {

		let allData = await getTestResults(
			`allSites`,
			localStorage.getItem('currentUser') ?? 'unknown'
		);

		let site = allData.find((site: {id: number}) => site.id === siteId);
		let location = site?.sampleLocationAllVOs.find((location: {id: number}) => location.id === tempId);

		if (location) {
			location.id = realId;

			await saveTestResults(
				`allSites`,
				allData,
				localStorage.getItem('currentUser') ?? 'unknown'
			);
		}
	}

	async function addNewLocationToCache(locationVO: any, incomingColumns: any) {

		let allData = await getTestResults(
			`allSites`,
			localStorage.getItem('currentUser') ?? 'unknown'
		);

		let site = allData.find((site: {id: number}) => site.id === siteId);

		site.sampleLocationAllVOs.push(locationVO);

		//Update the table columns of the site while you're here
		let existingCols = site.tableColumnSettingsAllVOs;
		incomingColumns.forEach((newCol: any) => {
			let prev = existingCols.find((existingCol: any) => existingCol.colName === newCol.colName);
			if (!prev && (newCol.desktop || newCol.mobile)) {
				existingCols.push({
					colName: newCol.colName,
					colType: newCol.colType,
					visible: true
				})
			}
		})

		await saveTestResults(
			`allSites`,
			allData,
			localStorage.getItem('currentUser') ?? 'unknown'
		);

		dispatch(setAllSitesData(allData)); //Site cache sometimes removes locations if you don't dispatch this
		
	}

	let duplicateName = false;
	function checkDuplicates(location: ILocations) {
		if (location.name === newLocation.name) {
			duplicateName = true;
		}
	}

	const handleConfirm = async () => {
		if (newLocation.name === '')
			return setLocationError('Add a name for this sample location.');
		duplicateName = false;
		locations.forEach(checkDuplicates);
		if (duplicateName) 
			return setLocationError('Please choose a unique location name.');
		if (newLocation.testKitDescriptors.length === 0)
			return setTestKitsError('Which test kit(s) will you run at this location?');
		setShowConfirmationLoading(true);
		await addNewLocation();
	};

	const handleSelectedKits = (selectedKits: any) => {
		resetErrors();
		setNewLocation(() => ({
			...newLocation,
			testKitDescriptors: selectedKits,
		}));
		setSelectedTestKits(selectedKits);
	};

	React.useEffect(() => {}, [showConfirmationLoading]);

	// Helper function to update columns cache
	const updateColumnsCache = async (testKits: any[], siteId: number, username: string) => {
		const incomingColumns = testKits.flatMap(testKit => [
			...testKit.testKitOutputDescriptorVOs
				.filter((outDesc: { hide: boolean }) => !outDesc.hide)
				.map((outDesc: { outputName: any }) => ({
					colName: outDesc.outputName,
					colType: "Output",
					desktop: true,
					mobile: true,
					siteId
				})),
			...testKit.testKitInputDescriptorVOs.map((inDesc: { inputName: any }) => ({
				colName: inDesc.inputName,
				colType: "Input",
				desktop: false,
				mobile: false,
				siteId
			}))
		]);

		const columnsDataCached = await getTestResults(`columns_data_${siteId}`, username) || [];
		const updatedColumns = [...columnsDataCached];
		
		incomingColumns.forEach(newCol => {
			if (!updatedColumns.some(existingCol => existingCol.colName === newCol.colName)) {
				updatedColumns.push(newCol);
			}
		});

		await saveTestResults(`columns_data_${siteId}`, updatedColumns, username);
	};

	return (
		<div id="new-location-inputs">
			<div className="mt-[8px]">
				<div className="border-b-[1px] border-[#E6E6E6] pb-[8px] mt-[8px]">
					<div
						className="
                grid grid-cols-12
                font-inter font-[600] text-[14px] tracking-[0.15px] leading-[17px] text-[#666666]
                pb-[5px]
              "
					>
						{headers.map((header) => (
							<div className={`${header.colSpan}`}>
								{header.name}
							</div>
						))}
					</div>
					<div
						className="
              grid grid-cols-12
              font-inter font-[600] text-[14px] tracking-[0.15px] leading-[17px] text-[#666666]
              pb-[8px]
            "
					>
						<div className="col-span-4 relative location-name">
							<TextField
								value={newLocation.name}
								size="small"
								style={{
									width: 320,
								}}
								onChange={handleLocationName}
								error={locationError !== ''}
								helperText={locationError}
							/>
						</div>
						<div className="col-span-4 test-kits">
							<TestKitsDropDownMultiselect
								data={testKits}
								selectedData={newLocation.testKitDescriptors}
								handleSelectedData={handleSelectedKits}
								error={testKitsError}
								size="small"
							/>
						</div>
						<div className="col-span-4 text-end self-center new-location-confirmation">
							<div
								className={`flex flex-row gap-[15px] justify-end ${
									showConfirmationLoading && 'cursor-progress'
								}`}
							>
								<div className="text-[14px] font-[600] text-[#556670] self-center">
									CONFIRM:
								</div>
								<ConfirmationButtons
									handleConfirm={handleConfirm}
									handleCancel={() =>
										setShowAddNewLocation(false)
									}
									disabled={showConfirmationLoading}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AddNewLocation;